import { createAsyncThunk } from "@reduxjs/toolkit";
import { specificationFoldersService } from "@/shared/services/api/specification-folders-service";
import { specificationFolderService } from "@/shared/services/api/specification-folder-services";
import { ISpecificationFoldersContentsRequest } from "@/shared/services/interface/requests/ISpecificationFoldersRequest";
import {
  ISpecificationFolderCreateRequest,
  ISpecificationFolderUpdateRequest,
  ISpecificationFolderDeleteRequest,
} from "@/shared/services/interface/requests/ISpecificationFolderRequest";
import { RootState } from "../store";
import {
  FilterType,
  ISortConfig,
  SpecificationStatusType,
} from "@/shared/services/interface/types/ISpecificationFoldersTypes";
import { specificationService } from "@/shared/services/api/specification-service";

interface IGetFoldersRequest {
  companyId: string;
  sortBy?: ISortConfig["sortBy"];
  order?: ISortConfig["order"];
  offset?: number;
  limit?: number;
  filter?: FilterType;
  hideLoadingEffect?: boolean;
  status?: SpecificationStatusType[];
  search?: string;
}

interface ICreateSpecificationRequest {
  companyId: string;
  folderId?: string;
  name?: string;
}

export const getRootFoldersThunk = createAsyncThunk(
  "specificationFolders/getRootFolders",
  async (data: IGetFoldersRequest, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const { sort, filter, status, search } = state.specificationFolders;

      const requestData: ISpecificationFoldersContentsRequest = {
        companyId: data.companyId,
        folderId: "root",
        sortBy: data.sortBy || sort.sortBy,
        order: data.order || sort.order,
        offset: data.offset || 0,
        limit: data.limit || 10,
        filter: data.filter || filter,
        status: data.status || status || [],
        search: data.search || search || "",
      };

      const response = await specificationFoldersService.getFolderContents(
        requestData
      );
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Root klasörler getirilemedi"
      );
    }
  }
);

export const getFolderContentsThunk = createAsyncThunk(
  "specificationFolders/getFolderContents",
  async (
    data: IGetFoldersRequest & {
      folderId: string;
      hideLoadingEffect?: boolean;
      loadMore?: boolean;
      hideLoadingMoreEffect?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const { sort, status, search } = state.specificationFolders;
      const currentLimit =
        state.specificationFolders.folderContents[data.folderId]?.pagination
          ?.limit || 10;
      const requestData: ISpecificationFoldersContentsRequest = {
        companyId: data.companyId,
        folderId: data.folderId,
        sortBy: sort.sortBy,
        order: sort.order,
        offset: 0,
        limit: data.limit || currentLimit || 10,
        filter: "all",
        status: status || [],
        search: data.search !== undefined ? data.search : (search || ""),
      };

      const response = await specificationFoldersService.getFolderContents(
        requestData
      );
      return { folderId: data.folderId, response };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        folderId: data.folderId,
        error: error?.message || "Klasör içeriği getirilemedi",
      });
    }
  }
);

export const createSpecificationFolderThunk = createAsyncThunk(
  "specificationFolders/createSpecificationFolder",
  async (data: ISpecificationFolderCreateRequest, thunkAPI) => {
    try {
      const response =
        await specificationFolderService.createSpecificationFolder(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Klasör oluşturulamadı"
      );
    }
  }
);

export const updateSpecificationFolderThunk = createAsyncThunk(
  "specificationFolders/updateSpecificationFolder",
  async (data: ISpecificationFolderUpdateRequest, thunkAPI) => {
    try {
      const response =
        await specificationFolderService.updateSpecificationFolder(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Klasör güncellenemedi"
      );
    }
  }
);

export const deleteSpecificationFolderThunk = createAsyncThunk(
  "specificationFolders/deleteSpecificationFolder",
  async (data: ISpecificationFolderDeleteRequest, thunkAPI) => {
    try {
      const response =
        await specificationFolderService.deleteSpecificationFolder(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.message || "Klasör silinemedi");
    }
  }
);

export const createSpecificationThunk = createAsyncThunk(
  "specificationFolders/createSpecification",
  async (data: ICreateSpecificationRequest, thunkAPI) => {
    try {
      const response = await specificationService.createSpecification(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Specification oluşturulamadı"
      );
    }
  }
);

/**
 * Move a specification to another folder.
 * Uses updateSpecification with name: MOVE_ONLY_NAME_SENTINEL so the backend
 * does not change the spec name (move-only). Prefer a dedicated move API that
 * accepts only folderId when available.
 */
const MOVE_ONLY_NAME_SENTINEL = "";

export const moveSpecificationToFolderThunk = createAsyncThunk(
  "specificationFolders/moveSpecificationToFolder",
  async (
    data: {
      companyId: string;
      specificationId: string;
      targetFolderId: string | null;
      sourceFolderId: string | null;
    },
    thunkAPI
  ) => {
    try {
      const response = await specificationService.updateSpecification({
        companyId: data.companyId,
        specificationId: data.specificationId,
        name: MOVE_ONLY_NAME_SENTINEL,
        folderId: data.targetFolderId,
      });
      return { ...response, sourceFolderId: data.sourceFolderId, targetFolderId: data.targetFolderId };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Şartname taşınamadı";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const moveFolderToFolderThunk = createAsyncThunk(
  "specificationFolders/moveFolderToFolder",
  async (
    data: {
      folderId: string;
      newParentId: string | null;
      oldParentId: string | null;
    },
    thunkAPI
  ) => {
    try {
      const response = await specificationFolderService.moveSpecificationFolder({
        folderId: data.folderId,
        newParentId: data.newParentId,
      });
      return { ...response, oldParentId: data.oldParentId, newParentId: data.newParentId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Klasör taşınamadı"
      );
    }
  }
);

export const getDeleteItemsFolderContentsThunk = createAsyncThunk(
  "specificationFolders/getDeleteItemsFolderContents",
  async (
    data: IGetFoldersRequest & {
      folderId: string;
      hideLoadingEffect?: boolean;
      loadMore?: boolean;
      hideLoadingMoreEffect?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const { sort } = state.specificationFolders;
      const currentLimit =
        state.specificationFolders.deleteItemsFolderContents[data.folderId]
          ?.pagination?.limit || 10;
      const requestData: ISpecificationFoldersContentsRequest = {
        companyId: data.companyId,
        folderId: data.folderId,
        sortBy: sort.sortBy,
        order: sort.order,
        offset: 0,
        limit: data.limit || currentLimit || 10,
        filter: "all",
        status: [],
        search: "",
      };

      const response = await specificationFoldersService.getFolderContents(
        requestData
      );
      return { folderId: data.folderId, response };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        folderId: data.folderId,
        error: error?.message || "Klasör içeriği getirilemedi",
      });
    }
  }
);
